import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from "react";

const STORAGE_KEY = "ihariprabodham-theme";
// Keep stored IDs stable: "cream" is now Light; "light" is now Purple.
const VALID_THEMES = new Set(["light", "dark", "cream"]);
const DEFAULT_THEME = "cream";
const ThemeContext = createContext(null);

function readTheme() {
  try {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    if (savedTheme !== null && !VALID_THEMES.has(savedTheme)) {
      console.warn("Ignoring an invalid saved site theme:", savedTheme);
      return DEFAULT_THEME;
    }
    return savedTheme ?? DEFAULT_THEME;
  } catch (error) {
    console.warn("Unable to read the saved site theme.", error);
    return DEFAULT_THEME;
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readTheme);
  const darkMode = theme === "dark";

  const setThemeByName = (nextTheme) => {
    if (!VALID_THEMES.has(nextTheme)) {
      console.warn("Ignoring an invalid requested site theme:", nextTheme);
      return;
    }
    setTheme(nextTheme);
  };

  const setDarkMode = (nextDarkMode) => {
    const resolvedDarkMode =
      typeof nextDarkMode === "function" ? nextDarkMode(darkMode) : nextDarkMode;
    setThemeByName(resolvedDarkMode ? "dark" : DEFAULT_THEME);
  };

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (error) {
      console.warn("Unable to save the site theme; it applies to this visit only.", error);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={[darkMode, setDarkMode, theme, setThemeByName]}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useDarkMode() {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error("useDarkMode must be used within ThemeProvider.");
  }
  return context;
}
